import { BreakPoint } from '@makemydeal/dr-activities-common';
import { Heading, Text } from '@makemydeal/dr-ui-bricks-components';
import { BreakPoints } from '@makemydeal/dr-ui-bricks-utils';
import styled from 'styled-components';

export const Wrapper = styled.div`
    border: ${({ theme }) => `1px solid ${theme.uiBricks?.defaultInputFieldDisabledFontColor || '#B2B2B2'}`};
    border-radius: 24px;
    padding: 16px;
    background: ${({ theme }) => theme.uiBricks?.pageBackgroundColor || '#ffffff'};

    @media (max-width: ${BreakPoints.PHONE}) {
        padding: 16px;
        border-radius: 16px;
    }
`;

export const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: ${({ theme }) => `1px solid ${theme.uiBricks?.inactiveBorderColor || '#cecfd1'}`};

    @media (max-width: ${BreakPoints.PHONE}) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        padding-bottom: 12px;
        margin-bottom: 12px;
    }
`;

/* ──────────────────── Optional Packages ──────────────────── */

export const PackagesSectionBlock = styled.div`
    margin-bottom: 8px;
`;

export const PackagesSectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

export const PackagesSectionTitle = styled(Heading)`
    margin: 0;
    color: ${({ theme }) => theme.uiBricks?.spZipcodeModalTitleColor || '#1d1d1e'};
`;

export const ExpandCollapseButton = styled(Text)`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.uiBricks?.primaryAccentColor || '#01336b'};
`;

export const PackageContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const AccordionWrapper = styled.div`
    background: ${({ theme }) => theme.uiBricks?.pageBackgroundColor || '#ffffff'};
    border-radius: 0;
    overflow: hidden;
    border-bottom: ${({ theme }) => `1px solid ${theme.uiBricks?.inactiveBorderColor || '#cecfd1'}`};
    padding: 8px;

    &:last-child {
        border-bottom: none;
    }
`;

export const AccordionHeader = styled.button<{ $isExpandable: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    background: transparent;
    border: none;
    box-shadow: none;
    cursor: ${({ $isExpandable }) => ($isExpandable ? 'pointer' : 'default')};
    text-align: left;
    padding-left: 0;
    padding-right: 0;
`;

export const HeaderLeft = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const PackageName = styled(Text)`
    vertical-align: bottom;
    color: #333436;
`;

export const ToggleIcon = styled.span<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.uiBricks?.primaryAccentColor || '#01336b'};
    transition: transform 0.2s ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    svg {
        width: 16px;
        height: 16px;
    }
`;

export const FeatureWrapper = styled.div`
    padding: 9px 0px 0px 0px;
`;

export const FeatureChipGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
`;

export const FeatureChip = styled(Text)`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    border: ${({ theme }) => `1px solid ${theme.uiBricks?.defaultInputFieldBorderColor || '#e4e5e6'}`};
    color: ${({ theme }) => theme.uiBricks?.sliderHeaderBackgroundColor || '#434447'};
    background: #f4f4f4;
`;

/* ──────────────────── Bottom Row ──────────────────── */

export const BottomRow = styled.div<{ $showTopBorder: boolean; $stacked: boolean }>`
    display: grid;
    grid-template-columns: ${({ $stacked }) => ($stacked ? '1fr' : 'repeat(auto-fit, minmax(0, 1fr))')};
    gap: ${({ $stacked }) => ($stacked ? '16px' : '24px')};
    border-top: ${({ $showTopBorder, theme }) =>
        $showTopBorder ? `1px solid ${theme.uiBricks?.packageCardBorderColor || '#000000'}` : 'none'};
    padding-top: ${({ $showTopBorder }) => ($showTopBorder ? '14px' : '0')};

    @media (max-width: ${BreakPoint.MEDIUM}) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

/* Standalone Factory Options */

export const StandaloneList = styled.div<{ $stacked: boolean }>`
    display: grid;
    grid-template-columns: ${({ $stacked }) => ($stacked ? '1fr' : 'repeat(2, 1fr)')};
    gap: 0;

    @media (max-width: ${BreakPoint.MEDIUM}) {
        grid-template-columns: 1fr;
    }
`;

export const StandaloneItem = styled(Text)`
    display: flex;
    justify-content: start;
    align-items: center;
    vertical-align: bottom;
    color: #333436;
    padding: 8px;
    border-bottom: ${({ theme }) => `1px solid ${theme.uiBricks?.inactiveBorderColor || '#cecfd1'}`};

    // An odd number of options leaves the last one alone in column 1, which would cut its bottom
    // border off halfway; span the whole row so the closing divider stays full width.
    &:last-child:nth-child(odd) {
        grid-column: 1 / -1;
    }
`;

/* Factory Options Added (Highlights) */

export const HighlightsColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

export const HighlightsTrimLabel = styled(Text)`
    margin: 6px 0 12px 0;
    color: ${({ theme }) => theme.uiBricks?.dashboardDrActivityDashboardRemoveCoBuyerCloseMsgSvgFill || '#727479'};
`;

export const HighlightsHeaderRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

export const HighlightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 24px;
    row-gap: 4px;

    @media (max-width: ${BreakPoint.MEDIUM}) {
        grid-template-columns: 1fr;
    }
`;

export const HighlightItem = styled(Text)`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    vertical-align: bottom;
    color: #333436;

    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

export const ViewAllLink = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const ViewAllLabel = styled(Text)`
    color: ${({ theme }) => theme.uiBricks?.primaryAccentColor || '#01336b'};
`;
